import { parseTime } from './clock.js';
import { createRng, hashSeed } from './rng.js';

/**
 * A real break schedule: people go to the break room, sit down, eat, look at
 * their phones, and go back.
 *
 * One break a shift, of `BREAK.minutes`, taken a little before the middle of
 * whoever's hours it is, a seeded spread either side so the room is never
 * everybody at once. They go from their post (only from their post, so the way
 * back ends where their route begins) along a path worked out over the
 * building with the main service run kept out of it (rule 2), sit or stand at
 * a spot in the break room for the rest of it, and walk the same path back.
 * While they are out they are still themselves: eyes, a shape, hours.
 *
 * Who takes one: anybody on the roster with a shift of five hours or more who
 * is not a door the game sends you to (a gate's hours are not allowed to be a
 * surprise: `onDutyAt`), and whom `exempt` does not keep at their post (the
 * one the story needs where he is, the one a search is for, anybody posted to
 * a job). Guests and visitors do not take breaks.
 *
 * Pure apart from what it asks the people to do. `pathFrom(point)` is the way
 * from a point to the break room, in metres, or null if there is none yet.
 */
export const BREAK = Object.freeze({
  /** How long, in game minutes, door to door. */
  minutes: 30,
  /** Where in the shift, as a share of its length. */
  at: 0.42,
  /** How far either side of that, at most, in game minutes. */
  spread: 75,
  /** The shortest shift that gets one, in game minutes. */
  shortest: 300,
});

/** The minutes a window lasts, wrapping midnight. */
function lengthOf(window) {
  const from = parseTime(window.from);
  let to = parseTime(window.to);
  if (to <= from) to += 1440;
  return to - from;
}

/** Whether somebody's row takes breaks at all. */
export function takesBreaks(row) {
  if (!row.window || row.gate || row.visitor || row.collects || row.feeds) return false;
  if (!row.department || row.department === 'guests') return false;
  return lengthOf(row.window) >= BREAK.shortest;
}

/**
 * When somebody's break starts on a day, in minutes since midnight (may run
 * past 1440 for a night shift), or null if they do not take one.
 */
export function breakStart(row, day, seed = 'lion') {
  if (!takesBreaks(row)) return null;
  const rng = createRng(hashSeed(seed, 'break', row.id, day));
  const from = parseTime(row.window.from);
  const middle = from + lengthOf(row.window) * BREAK.at;
  return Math.round((middle + (rng.next() * 2 - 1) * BREAK.spread) / 5) * 5;
}

export function createBreaks({ npcs, pathFrom, spots, seed = () => 'lion', exempt = () => false }) {
  /** Per person: where they are in today's break. */
  const states = new Map();

  const now = (clock) => clock.minutes;

  function stateOf(npc, day) {
    let state = states.get(npc.data.id);
    if (!state || state.day !== day) {
      // A break still on at midnight ends there, the way it would have.
      if (state?.phase === 'out') {
        npc.unpost({ home: false });
        npc.walkBack([...state.path.slice(0, -1).reverse(), { x: npc.route[0].x, z: npc.route[0].z }]);
      }
      state = { day, phase: 'due', path: null, spot: null };
      states.set(npc.data.id, state);
    }
    return state;
  }

  /** Inside today's break window, wrapping a night shift past midnight. */
  function within(start, minutes) {
    const end = start + BREAK.minutes;
    return (minutes >= start && minutes < end) || (minutes + 1440 >= start && minutes + 1440 < end);
  }

  let spotTurn = 0;
  /** Today's start for each person, worked out once a day rather than every frame. */
  const starts = new Map();
  let startsFor = null;
  function startOf(npc, day) {
    const key = `${day}|${seed()}`;
    if (startsFor !== key) {
      starts.clear();
      startsFor = key;
    }
    if (!starts.has(npc.data.id)) starts.set(npc.data.id, breakStart(npc.data, day, seed()));
    return starts.get(npc.data.id);
  }

  return {
    update(clock) {
      for (const npc of npcs) {
        const start = startOf(npc, clock.day);
        if (start === null) continue;
        const state = stateOf(npc, clock.day);
        const minutes = now(clock);
        if (!npc.present) {
          if (state.phase === 'out') state.phase = 'done';
          continue;
        }
        if (state.phase === 'due') {
          if (!within(start, minutes) || exempt(npc) || npc.posted || npc.aboard || npc.leaving || npc.returning?.length) continue;
          // Only from their post, so the way back ends where their route begins.
          const post = npc.route[0];
          if (Math.hypot(npc.position.x - post.x, npc.position.z - post.z) > 0.4) continue;
          const path = pathFrom(npc.position);
          if (!path || path.length < 2) continue;
          const spot = spots[spotTurn % spots.length];
          spotTurn += 1;
          const there = [...path.map((p) => ({ x: p.x, z: p.z, dwell: 0 })), ...(spot.via ?? []).map((p) => ({ ...p, dwell: 0 })), { x: spot.x, z: spot.z, dwell: 1e6 }];
          npc.post(there, { now: true });
          state.phase = 'out';
          state.path = there;
          state.spot = spot;
          continue;
        }
        if (state.phase === 'out' && !within(start, minutes)) {
          // Back the way they came, and their route picks up at their post.
          const back = state.path.slice(0, -1).reverse();
          npc.unpost({ home: false });
          npc.walkBack([...back, { x: npc.route[0].x, z: npc.route[0].z }]);
          state.phase = 'done';
        }
      }
    },

    /** Whether somebody is on their break: walking to it, or there. */
    onBreak(id) {
      return states.get(id)?.phase === 'out';
    },

    /** Where somebody is spending it, once they are on it. */
    spotOf(id) {
      const state = states.get(id);
      return state?.phase === 'out' ? state.spot : null;
    },

    /** Everybody out right now, for a check. */
    get out() {
      return [...states.entries()].filter(([, state]) => state.phase === 'out').map(([id]) => id);
    },

    /** Forgets today: everybody back at their posts, as after a reset. */
    clear() {
      for (const npc of npcs) if (states.get(npc.data.id)?.phase === 'out') npc.unpost();
      states.clear();
      starts.clear();
      startsFor = null;
      // Who sits where is dealt in turn: a reset deals from the top again,
      // or a seeded run sits people differently after any run before it.
      spotTurn = 0;
    },
  };
}
